/**
 * cartAdd
 * ------------------------------------------------------------------------------
 * Native add-to-cart for every surface, replacing the legacy `.wbc-form`
 * jQuery interception and direct `wbc.add()` calls.
 *
 * Each add: native `POST /cart/add.js` (form serialization) → analytics. The item
 * is added to the native Shopify cart; Rebuy's Smart Cart opens the drawer
 * (or openSmartCartWhenReady does, when the add came before Rebuy loaded).
 *
 * Exposed as ES exports AND on `window` (`hrAddToCart` / `hrSerializeCartForm`) so
 * inline snippet scripts (which can't import ES modules) can use it too.
 *
 * @namespace cartAdd
 */

import {refreshCartCount} from '@/scripts/components/cartCount';

// Rebuy's Smart Cart opens itself by watching cart requests, but it loads only on the
// first interaction (snippets/async-load-head-scripts). An add that starts before it is
// ready (a tap straight onto Add to Bag) goes unseen, so open the drawer once Rebuy is up.
let smartCartReady = false;
document.addEventListener('rebuy:smartcart.ready', () => {
    smartCartReady = true;
}, {once: true});

function showSmartCart() {
    const rebuy = window.Rebuy;
    if (typeof rebuy?.SmartCart?.show !== 'function') {
        return;
    }
    Promise.resolve(rebuy.Cart?.fetchCart?.())
        .catch(() => {})
        .then(() => rebuy.SmartCart.show());
}

function openSmartCartWhenReady() {
    if (smartCartReady) {
        showSmartCart();
        return;
    }
    document.addEventListener('rebuy:smartcart.ready', showSmartCart, {once: true});
}

const CART_FORM_SELECTOR = 'form[data-cart-form]';
const CART_ATC_SELECTOR = '[data-cart-atc][data-id]';

/**
 * Analytics parity with the old `wbc.add` / `wbc.klaviyo` (fired for every surface).
 * NOTE: the Google Ads gtag conversion stays surface-specific (PDP fires its own).
 * @param {object} response - the /cart/add.js response ({items:[...]})
 */
function trackAdd(response) {
    try {
        if (typeof window.letterpress === 'function') {
            window.letterpress('trackStructEvent', 'cart', 'add');
        }
    } catch (error) {
        /* noop */
    }

    try {
        if (typeof window.GAevent === 'function') {
            window.GAevent('Cart', 'Add To Cart', 'Cart Added To Cart');
        }
    } catch (error) {
        /* noop */
    }

    try {
        const items = (response && response.items) || [];
        // Klaviyo loads on the first interaction (snippets/async-load-head-scripts) and replays this queue.
        window._learnq = window._learnq || [];
        if (Array.isArray(window._learnq) || typeof window._learnq.push === 'function') {
            items.forEach((item) => {
                window._learnq.push(['track', 'Added to Cart', {
                    Name: item.title,
                    ProductID: item.product_id,
                    ImageURL: item.image,
                    URL: item.url,
                    Brand: 'Henry Rose',
                    Price: item.price,
                }]);
            });
        }
    } catch (error) {
        /* noop */
    }
}

/**
 * POST items to /cart/add.js and track.
 * @param {Array<{id:number, quantity:number, properties?:object}>} items
 * @param {object} [options]
 * @param {boolean} [options.silent] - Suppress the failure `alert()`. For
 *   surfaces that render the error themselves; the 422 body is still returned,
 *   so the caller can show Shopify's own `description`. Defaults to false, so
 *   every existing call site keeps the alert.
 * @returns {Promise<object|null>} the add response
 */
export async function addToCart(items, {silent = false} = {}) {
    if (!items || !items.length || !items[0].id) {
        console.error('[cartAdd] no items / variant id', items);
        return null;
    }

    const smartCartMissedAdd = !smartCartReady;

    const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({items}),
    });

    // The Ajax API answers /cart/add.js with `text/javascript`, not
    // `application/json`, so gating the parse on the header threw the body away
    // on every call — callers got null, `trackAdd` never saw a line item, and a
    // 422's real message was replaced by the generic fallback below.
    let data = null;
    try {
        data = await response.json();
    } catch (error) {
        data = null;
    }

    // Shopify returns 422 with a JSON body when the line can't be added.
    if (!response.ok || (data && data.status === 422)) {
        const message = (data && (data.description || data.message)) || 'Could not add to cart.';

        if (!silent) {
            alert(message);
        }

        return data;
    }

    trackAdd(data);
    // The /cart/add.js response only holds the added lines, not the cart total,
    // so refetch to sync every [data-cart-count] badge.
    refreshCartCount();

    if (smartCartMissedAdd) {
        openSmartCartWhenReady();
    }

    return data;
}

/**
 * Variant id resolution, in the same priority order as the legacy wbc.init.form():
 * active pill → hidden first-id → .variant-select → select[name="id"] → name="id".
 * @param {HTMLFormElement} form
 * @returns {string|null}
 */
function resolveVariantId(form) {
    const activePill = form.querySelector('[data-id].active');
    if (activePill) {
        return activePill.getAttribute('data-id');
    }

    const firstId = form.querySelector('[data-first_id]');
    if (firstId) {
        return firstId.getAttribute('data-first_id');
    }

    const variantSelect = form.querySelector('.variant-select');
    if (variantSelect && variantSelect.value) {
        return variantSelect.value;
    }

    // Must run before the generic [name="id"] lookup below: the cross-sell form
    // (cross-sell-product-card.liquid) has a hidden input[name="id"] holding the FIRST
    // variant id ahead of the <select name="id"> in DOM order, so the generic lookup
    // would return the first variant instead of the shopper's choice.
    // A <select>'s .value is natively the selected option's value — jQuery's
    // `option:selected` pseudo-class is not valid CSS and throws in querySelector.
    const idSelect = form.querySelector('select[name="id"]');
    if (idSelect && idSelect.value) {
        return idSelect.value;
    }

    const idInput = form.querySelector('[name="id"]');
    return idInput ? idInput.value : null;
}

/**
 * Resolve {id, quantity, properties} from a cart form. Ports wbc.init.form()'s
 * resolution (cart-wb-js.liquid:1291) so the legacy pill / name="id" select forms
 * keep working, including the multi-select bundle `_option_{i}_*` property expansion.
 * @param {HTMLFormElement} form
 */
export function serializeCartForm(form) {
    const id = parseInt(resolveVariantId(form), 10);

    const quantityEl = form.querySelector('[data-quantity]') || form.querySelector('[name="quantity"]');
    const quantity = (quantityEl && parseInt(quantityEl.value, 10)) || 1;

    const properties = Array.from(form.querySelectorAll('[name^="properties["][name$="]"]')).reduce((acc, el) => {
        const name = el.name.replace('properties[', '').replace(']', '');
        const value = el.value;
        if (!name || !value) {
            return acc;
        }

        // Bundle multi-selects carry the chosen component's ids as data-* on the option.
        const expanded = Array.from(el.selectedOptions || []).reduce((carry, option) => {
            const index = parseInt(option.dataset.optionIndex, 10);
            if (Number.isNaN(index)) {
                return carry;
            }
            return {
                ...carry,
                [`_option_${index}_variant_id`]: option.dataset.variantId,
                [`_option_${index}_product_id`]: option.dataset.productId,
                [`_option_${index}_sku`]: option.dataset.variantSku,
            };
        }, {});

        return {...acc, ...expanded, [name]: value};
    }, {});

    return {id, quantity, properties};
}

function onCartFormSubmit(event) {
    const form = event.target;
    if (!form || typeof form.matches !== 'function' || !form.matches(CART_FORM_SELECTOR)) {
        return;
    }

    event.preventDefault();
    // Defeat any other submit handler still bound to this form (e.g. legacy wbc).
    event.stopImmediatePropagation();

    const {id, quantity, properties} = serializeCartForm(form);
    if (!id || Number.isNaN(id)) {
        console.error('[cartAdd] could not resolve variant id for', form);
        return;
    }

    const item = {id, quantity};
    if (properties && Object.keys(properties).length) {
        item.properties = properties;
    }

    const button = form.querySelector('.pdp-form__submit') || form.querySelector('[type="submit"]');
    const label = button && button.querySelector('.pdp-button-label');
    const prevLabel = label ? label.innerHTML : null;
    if (button) {
        button.disabled = true;
    }
    if (label) {
        label.textContent = '...';
    }

    addToCart([item])
        .catch((error) => console.error('[cartAdd] add failed', error))
        .finally(() => {
            if (button) {
                button.disabled = false;
            }
            if (label && prevLabel !== null) {
                label.innerHTML = prevLabel;
            }
        });
}

function onCartAtcClick(event) {
    const button = event.target.closest && event.target.closest(CART_ATC_SELECTOR);
    if (!button) {
        return;
    }

    event.preventDefault();
    const id = parseInt(button.getAttribute('data-id'), 10);
    if (!id || Number.isNaN(id)) {
        return;
    }

    button.disabled = true;
    addToCart([{id, quantity: 1}])
        .catch((error) => console.error('[cartAdd] add failed', error))
        .finally(() => {
            button.disabled = false;
        });
}

let initialized = false;

/**
 * Attach the delegated handlers once. Capture phase for submit so we run (and
 * preventDefault) before any form-level listener.
 */
export function initCartForms() {
    if (initialized) {
        return;
    }
    initialized = true;

    document.addEventListener('submit', onCartFormSubmit, true);
    document.addEventListener('click', onCartAtcClick);
}

if (typeof window !== 'undefined') {
    window.hrAddToCart = addToCart;
    window.hrSerializeCartForm = serializeCartForm;
    initCartForms();
}
